import { create } from 'zustand';

const defaultState: PurchaseDrawerStoreState = {
    isDrawerOpen: false,
    drawerProductList: [],
    drawerPickedProductMap: new Map(),
    addonProductList: [],
    addonPickedProductMap: new Map(),
    isSetProduct: false,
    setRequiredSelectionCount: 0,
    representProductCharge: 0,
    productOptions: [],
};
export const usePurchaseDrawerStore = create<PurchaseDrawerStoreState & PurchaseDrawerStoreAction>()((set) => ({
    ...defaultState,
    action: {
        setIsDrawerOpen: (isDrawerOpen) => set(() => ({ isDrawerOpen })),
        setDrawerProductList: (drawerProductList) => set(() => ({ drawerProductList })),
        setDrawerPickedProductMap: (drawerPickedProductMap) =>
            set(() => ({ drawerPickedProductMap: drawerPickedProductMap })),
        setAddonProductList: (addonProductList) => set(() => ({ addonProductList })),
        setAddonPickedProductMap: (addonPickedProductMap) => set(() => ({ addonPickedProductMap })),
        setSetProductInfo: (isSetProduct, setRequiredSelectionCount, representProductCharge, productOptions) =>
            set(() => ({ isSetProduct, setRequiredSelectionCount, representProductCharge, productOptions })),
        clear: () =>
            set(() => ({
                isDrawerOpen: false,
                drawerProductList: [],
                drawerPickedProductMap: new Map(),
                addonProductList: [],
                addonPickedProductMap: new Map(),
            })),
    },
}));

export interface PurchaseDrawerStoreState {
    isDrawerOpen: boolean;
    isSetProduct: boolean;
    setRequiredSelectionCount: number;
    representProductCharge: number;
    productOptions?: {
        optionKey: string;
        optionDetail: {
            optionValue: string;
            soldOutStatus: boolean;
        }[];
        displayOrder: number;
    }[];
    drawerProductList: {
        productGroupId: string;
        productId: string;
        productCharge: number;
        productPrice: number;
        inventory: number;
        options: {
            optionKey: string;
            optionValue: string;
        }[];
        representProductId: string;
        representProductImage: string;
        representProductName: string;
        representProductPrice: number;
        totalCharge: number;
        isForSale: boolean;
        discountMethod: string;
        discountValue: number;
        ownerType: 'SELLER' | 'VENDOR';
        productShippingPolicy: {
            shippingCost: number;
            islandMountainShippingCost: number;
            freeShippingCostBar: number;
            islandMountainDiscountShippingCostBar: number;
            islandMountainDiscountedPrice: number;
            jejuShippingCost: number;
            jejuDiscountShippingCostBar: number;
            jejuDiscountedPrice: number;
        };
        vendorInfo: {
            id: string;
            name: string;
            customerCenterNumber: string;
            returnAddress: {
                postCode: string;
                recipientAddressDetail: string;
                recipientAddress: string;
            };
        } | null;
    }[];
    addonProductList: {
        productGroupId: string;
        productId: string;
        optionName: string;
        optionValue: string;
        productCharge: number;
        inventory: number;
        order: number;
        ownerType: 'SELLER' | 'VENDOR';
        representProductId: string;
        representProductImage: string;
        representProductName: string;
        representProductPrice: number;
        productShippingPolicy: {
            shippingCost: number;
            islandMountainShippingCost: number;
            freeShippingCostBar: number;
            islandMountainDiscountShippingCostBar: number;
            islandMountainDiscountedPrice: number;
            jejuShippingCost: number;
            jejuDiscountShippingCostBar: number;
            jejuDiscountedPrice: number;
            id?: string;
            isBundle?: boolean;
            type?: 'CONDITIONAL_FREE' | 'FREE' | 'CHARGE';
        };
        vendorInfo: {
            id: string;
            name: string;
            customerCenterNumber: string;
            returnAddress: {
                postCode: string;
                recipientAddressDetail: string;
                recipientAddress: string;
            };
        } | null;
    }[];
    addonPickedProductMap: Map<
        string,
        {
            representProductId: string;
            representProductImage: string;
            representProductName: string;
            representProductPrice: number;
            productGroupId: string;
            productId: string;
            optionName: string;
            optionValue: string;
            productCharge: number;
            inventory: number;
            numberOfPicked: number;
            totalCharge: number;
            ownerType: 'SELLER' | 'VENDOR';
            productShippingPolicy: {
                shippingCost: number;
                islandMountainShippingCost: number;
                freeShippingCostBar: number;
                islandMountainDiscountShippingCostBar: number;
                islandMountainDiscountedPrice: number;
                jejuShippingCost: number;
                jejuDiscountShippingCostBar: number;
                jejuDiscountedPrice: number;
                id?: string;
                isBundle?: boolean;
                type?: 'CONDITIONAL_FREE' | 'FREE' | 'CHARGE';
            };
            vendorInfo: {
                id: string;
                name: string;
                customerCenterNumber: string;
                returnAddress: {
                    postCode: string;
                    recipientAddressDetail: string;
                    recipientAddress: string;
                };
            } | null;
        }
    >;
    drawerPickedProductMap: Map<
        string,
        {
            productGroupId: string;
            productId?: string;
            productIds?: string[];
            productCharge: number;
            inventory: number;
            numberOfPicked: number;
            optionName: string;
            productPrice: number;
            representProductId: string;
            representProductImage: string;
            representProductName: string;
            representProductPrice: number;
            totalCharge: number;
            isForSale: boolean;
            discountMethod: string;
            discountValue: number;
            ownerType: 'SELLER' | 'VENDOR';
            productShippingPolicy: {
                shippingCost: number;
                islandMountainShippingCost: number;
                freeShippingCostBar: number;
                islandMountainDiscountShippingCostBar: number;
                islandMountainDiscountedPrice: number;
                jejuShippingCost: number;
                jejuDiscountShippingCostBar: number;
                jejuDiscountedPrice: number;
                id?: string;
                isBundle?: boolean;
                type?: 'CONDITIONAL_FREE' | 'FREE' | 'CHARGE';
            };
            vendorInfo: {
                id: string;
                name: string;
                customerCenterNumber: string;
                returnAddress: {
                    postCode: string;
                    recipientAddressDetail: string;
                    recipientAddress: string;
                };
            } | null;
        }
    >;
}
interface PurchaseDrawerStoreAction {
    action: {
        setIsDrawerOpen: (isDrawerOpen: boolean) => void;
        setDrawerProductList: (drawerProductList: PurchaseDrawerStoreState['drawerProductList']) => void;
        setAddonProductList: (addonProductList: PurchaseDrawerStoreState['addonProductList']) => void;
        setSetProductInfo: (
            isSetProduct: boolean,
            setRequiredSelectionCount: number,
            representProductCharge: number,
            productOptions: PurchaseDrawerStoreState['productOptions'],
        ) => void;
        setDrawerPickedProductMap: (drawerPickedProductMap: PurchaseDrawerStoreState['drawerPickedProductMap']) => void;
        setAddonPickedProductMap: (addonPickedProductMap: PurchaseDrawerStoreState['addonPickedProductMap']) => void;
        clear: () => void;
    };
}
